import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, message, Alert, Button, Spin, Timeline } from 'antd';
import { FD120, FD125 } from '../../services/d202Api';
import { d202Api } from '../../services/d202Api';
import { useD202Mode } from './hooks/useD202Mode';
import { useD202Lookup } from './hooks/useD202Lookup';
import { useD202Record } from './hooks/useD202Record';
import { useD202Navigation } from './hooks/useD202Navigation';
import { useD202Permission } from './hooks/useD202Permission';
import { useD202Approval } from './hooks/useD202Approval';
import PermissionGuard from '../../components/PermissionGuard';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import { BatchAcceptanceBridge, pwd202f1AcceptanceManifest } from '../../features/acceptance';
import D202MasterFields from './components/D202MasterFields';
import D202DetailTable from './components/D202DetailTable';
import D202AbnormalTab from './components/D202AbnormalTab';
import { getStatusBadge, isFieldDisabledFn } from './helpers';
import './D202Form.css';

// ---- Component ----
const D202Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewNo = searchParams.get('review');

  const [activeTab, setActiveTab] = useState(0);
  const [abnormals, setAbnormals] = useState<Partial<FD125>[]>([]);

  // ---- Hooks ----
  const perms = useD202Permission();
  const { mode, setMode, isEditing } = useD202Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useD202Lookup();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    importPurchaseOrder, importInternalShipment,
    getFieldError,
    lastApiResponse,
  } = useD202Record();

  // Wrap loadRecord to also resolve names and populate abnormals
  const loadRecordWithNames = useCallback(async (receiptNo: string) => {
    const result = await loadRecord(receiptNo);
    if (result) {
      resolveAllNames(result.data, result.dets[0]);
      setAbnormals(result.data.abnormals || []);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useD202Navigation(loadRecordWithNames);

  // Approval
  const {
    handleSubmitClick,
    handleApproveClick,
    handleRejectClick,
    handleShowHistory,
    handleWriteoff,
    historyVisible,
    setHistoryVisible,
    historyLoading,
    historyEvents,
  } = useD202Approval({ record, isEditing, loadRecordWithNames });

  // Wrapped approve/reject that also navigate back if in review mode
  const handleApproveWithReview = useCallback(async () => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准繳庫單 ${record.fd120_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d202Api.approve(record.fd120_01);
          message.success(resp.message || '核准成功');
          if (reviewNo) {
            navigate('/erp/approvals');
          } else {
            await loadRecordWithNames(record.fd120_01);
          }
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, reviewNo, navigate, loadRecordWithNames]);

  const handleRejectWithReview = useCallback(async () => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回繳庫單 ${record.fd120_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d202Api.reject(record.fd120_01);
          message.success(resp.message || '退回成功');
          if (reviewNo) {
            navigate('/erp/approvals');
          } else {
            await loadRecordWithNames(record.fd120_01);
          }
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, reviewNo, navigate, loadRecordWithNames]);

  // ---- Initial Load ----
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        const rn = searchParams.get('review');
        if (rn) {
          const idx = ids.findIndex(r => r === rn);
          const targetIdx = idx >= 0 ? idx : 0;
          setCurrentIdx(targetIdx);
          if (ids[targetIdx]) await loadRecordWithNames(ids[targetIdx]);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load D202 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve detail-level names when selected row changes
  useEffect(() => {
    const d = details[selectedRow];
    if (d) {
      resolveSingleName('grade', 'grade', String(d.fd121_14 || ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, details]);

  // ---- Mode Actions ----
  const startAdd = useCallback(() => {
    setCurrentIdx(-1);
    setMode('add');
    resetForAdd();
    setActiveTab(0);
    clearNames();
    setAbnormals([]);
  }, [setCurrentIdx, setMode, resetForAdd, clearNames]);

  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: FD120) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fd120_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      startAdd();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, startAdd]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除繳庫單 ${record.fd120_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    const url = d202Api.getPrintUrl(record.fd120_01);
    window.open(url, '_blank');
  }, [record, isEditing]);

  // ---- Approval state for SharedActionBar ----
  const statusCode = (record?.fd120_80 || formData?.fd120_80 || '').trim();
  const statusBadge = getStatusBadge(record || formData);
  const approvalState: ApprovalState = {
    statusCode,
    isApproved: statusCode === 'P',
    isPending: ['D', 'S'].includes(statusCode),
    isLocked: statusCode === 'P',
  };

  const isFieldDisabled = useCallback((key: string) => isFieldDisabledFn(key, isEditing, mode), [isEditing, mode]);

  // ---- Render ----
  return (
    <PermissionGuard module="pwd202f1" action="query">
    <div className="d202-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">

        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>採購管理</a>
              <span>/</span>
              <span className="current">繳庫單</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>繳庫單 Warehouse Receipt</h1>
            <span className="badge badge-default">D202</span>
            {statusBadge && (
              <span
                className={`badge field-label ${statusBadge.cls}`}
                data-contract-field="fd120_80"
              >
                核准狀態：{statusBadge.text}
              </span>
            )}
          </div>
        </div>

        {/* Review banner */}
        {reviewNo && (
          <Alert
            message="您正在審核此單據"
            description={`單號: ${reviewNo}。核准或退回後將自動返回待審核清單。`}
            type="warning"
            showIcon
            banner
            style={{ marginBottom: 12 }}
          />
        )}

        {/* Search Bar */}
        <div className="search-bar">
          <div className="field-group">
            <label>繳庫單號</label>
            <input
              type="text"
              value={String(getField('fd120_01') || '')}
              disabled
              readOnly
              style={{ width: 120 }} data-contract-field="fd120_01"
            />
          </div>
          <div className="field-group">
            <label>採購單號</label>
            <input
              type="text"
              value={String(getField('fd120_21') || '')}
              disabled
              readOnly
              style={{ width: 120 }} data-contract-field="fd120_21"
            />
          </div>
          <div className="field-group">
            <label data-contract-field="fd120_12">廠商</label>
            <span style={{ fontSize: 13 }}>{names.supplier || ''}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '繳庫單明細', '異常說明'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 單頭資料 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <D202MasterFields
              formData={formData}
              names={names}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
              getFieldError={getFieldError}
              importPurchaseOrder={importPurchaseOrder}
              importInternalShipment={importInternalShipment}
            />
          </div>

          {/* Tab 2: 產品明細 */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <D202DetailTable
              details={details}
              selectedRow={selectedRow}
              names={names}
              isEditing={isEditing}
              setSelectedRow={setSelectedRow}
              getDetailField={getDetailField}
              setDetailField={setDetailField}
              resolveSingleName={resolveSingleName}
              addDetailRow={addDetailRow}
              deleteDetailRow={deleteDetailRow}
            />
          </div>

          {/* Tab 3: 異常說明 */}
          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <D202AbnormalTab
              formData={formData}
              abnormals={abnormals}
              isEditing={isEditing}
              getField={getField}
              setField={setField}
            />
          </div>

        </div>{/* /tabs */}

        {/* Spacer for fixed action bar */}
        <div style={{ height: 64 }} />

        {/* Shared Action Bar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => handleNavTo(0)}
          onNavPrev={() => handleNavTo(currentIdx - 1)}
          onNavNext={() => handleNavTo(currentIdx + 1)}
          onNavLast={() => handleNavTo(allIds.length - 1)}
          onAdd={startAdd}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onSave={handleAddAction}
          onCancel={handleModifyAction}
          approval={approvalState}
          onSubmit={handleSubmitClick}
          onApprove={handleApproveWithReview}
          onReject={handleRejectWithReview}
          onWriteoff={handleWriteoff}
          onHistory={handleShowHistory}
          onPrint={handlePrint}
          onExit={() => navigate('/erp')}
          canCreate={perms.canCreate}
          canEdit={perms.canEdit}
          canDelete={perms.canDelete}
          canApprove={perms.canApprove}
          canPrint={perms.canPrint}
          acceptanceActionIds={{
            list: 'PWD202F1-ACT-LIST',
            get: 'PWD202F1-ACT-GET',
            create: 'PWD202F1-ACT-CREATE',
            update: 'PWD202F1-ACT-UPDATE',
            delete: 'PWD202F1-ACT-DELETE',
            submit: 'PWD202F1-ACT-SUBMIT',
            approve: 'PWD202F1-ACT-APPROVE',
            reject: 'PWD202F1-ACT-REJECT',
            writeoff: 'PWD202F1-ACT-WRITEOFF',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwd202f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fd120_01 ? [record.fd120_01] : []}
          keyOf={(item) => item.fd120_01}
          queryExact={(key) => d202Api.get(key)}
          lastApiResponse={lastApiResponse}
          loading={loading}
          onAutoFill={() => allIds[0] && handleNavTo(0)}
          autoFillDisabled={allIds.length === 0}
          autoFillReason="載入正式 API 已存在的第一筆繳庫單；來源單帶入、儲存與核准仍走原表單 API。"
          screenDraft={{ master: formData, details, abnormals }}
        />

      </div>{/* /page-wrapper */}

      {/* Approval History Modal */}
      <Modal
        title="審核歷史"
        open={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={<Button onClick={() => setHistoryVisible(false)}>關閉</Button>}
      >
        {historyLoading ? (
          <Spin />
        ) : (
          <Timeline
            items={historyEvents.map(ev => ({
              color: ev.action === '核准' ? 'green' : ev.action === '退回' ? 'red' : 'blue',
              children: (
                <div>
                  <strong>{ev.action}</strong>
                  {ev.user && ` by ${ev.user}`}
                  {ev.date && ` ${ev.date}`}
                  {ev.note && <div style={{ color: '#888', fontSize: 12 }}>{ev.note}</div>}
                </div>
              ),
            }))}
          />
        )}
      </Modal>

    </div>
    </PermissionGuard>
  );
};

export default D202Form;
